import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Divider,
  IconButton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Assignment as BillIcon,
  Payment as PayrollIcon,
  Work as WorkIcon,
  CloudDownload as ImportIcon,
  Receipt as InvoiceIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import billingService, {
  BillingPeriod,
  BillingPeriodStatus,
  Bill,
  Payroll,
  CompletedWorkBilling,
} from '../../services/newBillingService';
import CompletedWorkView from '../../components/billing/CompletedWorkView';
import BillsView from '../../components/billing/BillsView';
import PayrollView from '../../components/billing/PayrollView';
import PageLayout from '@/components/Layout/PageLayout';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`billing-tabpanel-${index}`}
      aria-labelledby={`billing-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const BillingPeriodDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod | null>(null);
  const [completedWork, setCompletedWork] = useState<CompletedWorkBilling[]>([]);
  const [bills, setBills] = useState<Bill[]>([]);
  const [payrolls, setPayrolls] = useState<Payroll[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (id) {
      loadBillingPeriodData(parseInt(id));
    }
  }, [id]);

  const loadBillingPeriodData = async (periodId: number) => {
    try {
      setLoading(true);
      setError(null);

      // Load billing period details
      const periodResponse = await billingService.billingPeriod.getBillingPeriod(periodId);
      if (periodResponse.success && periodResponse.data) {
        setBillingPeriod(periodResponse.data);
      }

      let allCompletedWork: CompletedWorkBilling[] = [];

      // First, try to load imported completed work (both billed and unbilled)
      try {
        const importedWorkResponse = await billingService.completedWork.getCompletedWorkForBillingPeriod(periodId, false);
        if (importedWorkResponse.success && importedWorkResponse.data && importedWorkResponse.data.length > 0) {
          allCompletedWork = importedWorkResponse.data;
        }
      } catch (err) {
        console.log('No imported work found, will load from roster API');
      }

      // If no imported work found, load available work from roster API
      if (allCompletedWork.length === 0) {
        try {
          const availableWorkResponse = await billingService.completedWork.getAvailableCompletedWork(periodId);
          if (availableWorkResponse.success && availableWorkResponse.data) {
            // Convert ShiftDataDto to CompletedWorkBilling format for display
            allCompletedWork = availableWorkResponse.data.map((shift: any) => ({
              id: shift.shiftId,
              shiftId: shift.shiftId,
              staffId: shift.staffId,
              staffName: shift.staffName,
              employeeId: `EMP-${shift.staffId.toString().padStart(4, '0')}`,
              clientId: shift.clientId,
              clientName: `Client-${shift.clientId}`,
              shiftDate: shift.startDateTime.split('T')[0],
              actualStartTime: shift.startDateTime,
              actualEndTime: shift.endDateTime,
              totalHours: shift.durationHours,
              regularHours: shift.durationHours,
              overtimeHours: 0,
              hourlyRate: shift.hourlyRate,
              totalAmount: shift.totalAmount,
              approvedBy: 0, // Default value
              approvedAt: new Date().toISOString(), // Default value
              workNotes: shift.notes,
              isBilled: shift.isBilled,
              billingPeriodId: periodId,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            }));
          }
        } catch (err) {
          console.error('Error loading available work:', err);
        }
      }

      setCompletedWork(allCompletedWork);

      // Load bills
      const billsResponse = await billingService.billGeneration.getBillsForPeriod(periodId);
      if (billsResponse.success && billsResponse.data) {
        setBills(billsResponse.data);
      }

      // Load payrolls
      const payrollsResponse = await billingService.payroll.getPayrollsForPeriod(periodId);
      if (payrollsResponse.success && payrollsResponse.data) {
        setPayrolls(payrollsResponse.data);
      }

    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBills = async () => {
    if (!billingPeriod) return;

    try {
      setGenerating(true);
      setError(null);

      console.log('Starting bill generation for billing period:', billingPeriod.id);
      const response = await billingService.completedWork.generateBills(billingPeriod.id);
      console.log('Generate bills response:', response);
      
      if (response.success) {
        console.log('Bills generated successfully, reloading data...');
        // Reload data to show new bills
        await loadBillingPeriodData(billingPeriod.id);
        setActiveTab(1); // Switch to Bills tab
        console.log('Generate bills completed successfully');
      } else {
        console.error('Generate bills failed:', response.message);
        setError(response.message || 'Failed to generate bills');
      }
    } catch (err: any) {
      console.error('Generate bills error:', err);
      setError(err.message || 'Failed to generate bills');
    } finally {
      setGenerating(false);
    }
  };

  const handleImportCompletedWork = async () => {
    if (!billingPeriod) return;

    try {
      setImporting(true);
      setError(null);

      console.log('Starting import for billing period:', billingPeriod.id);
      const response = await billingService.completedWork.importCompletedWork(billingPeriod.id);
      
      console.log('Import response:', response);
      
      if (response.success) {
        console.log('Import successful, reloading data...');
        // Reload data to show imported work
        await loadBillingPeriodData(billingPeriod.id);
        setActiveTab(0); // Switch to Completed Work tab
        
        // Show success message with details
        if (response.data) {
          const { importedCount, skippedCount, errorCount } = response.data;
          const message = `Import completed: ${importedCount} imported, ${skippedCount} skipped, ${errorCount} errors`;
          console.log(message);
          // You could show this in a snackbar or alert if needed
        }
      } else {
        setError(response.message || 'Import failed');
      }
    } catch (err: any) {
      console.error('Import error:', err);
      setError(err.message || 'Failed to import completed work');
    } finally {
      setImporting(false);
    }
  };

  const getStatusColor = (status: BillingPeriodStatus): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status) {
      case BillingPeriodStatus.Active: return 'success';
      case BillingPeriodStatus.Closed: return 'warning';
      case BillingPeriodStatus.Billed: return 'info';
      case BillingPeriodStatus.Completed: return 'default';
      default: return 'default';
    }
  };

  const getStatusText = (status: BillingPeriodStatus): string => {
    switch (status) {
      case BillingPeriodStatus.Active: return 'Active';
      case BillingPeriodStatus.Closed: return 'Closed';
      case BillingPeriodStatus.Billed: return 'Billed';
      case BillingPeriodStatus.Completed: return 'Completed';
      default: return 'Unknown';
    }
  };

  const canGenerateBills = (): boolean => {
    return billingPeriod?.status === BillingPeriodStatus.Closed && 
           completedWork.some(work => !work.isBilled);
  };

  const canImportWork = (): boolean => {
    return billingPeriod?.status === BillingPeriodStatus.Active ||
           billingPeriod?.status === BillingPeriodStatus.Closed;
  };

  const unbilledWorkCount = completedWork.filter(work => !work.isBilled).length;
  const totalWorkHours = completedWork.reduce((sum, work) => sum + work.totalHours, 0);
  const totalWorkAmount = completedWork.reduce((sum, work) => sum + work.totalAmount, 0);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!billingPeriod) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Billing period not found
        </Alert>
      </Box>
    );
  }

  return (
    <PageLayout>
        <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/billing/periods')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
          {billingPeriod.name}
        </Typography>
        <Chip
          label={getStatusText(billingPeriod.status)}
          color={getStatusColor(billingPeriod.status)}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Period Summary
          </Typography>
          <Box display="flex" gap={4} flexWrap="wrap">
            <Box>
              <Typography variant="body2" color="text.secondary">
                Period
              </Typography>
              <Typography variant="body1">
                {format(new Date(billingPeriod.startDate), 'MMM dd, yyyy')} - {format(new Date(billingPeriod.endDate), 'MMM dd, yyyy')}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Total Work Hours
              </Typography>
              <Typography variant="body1">
                {totalWorkHours.toFixed(2)} hours
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Total Work Amount
              </Typography>
              <Typography variant="body1">
                ${totalWorkAmount.toFixed(2)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Unbilled Work
              </Typography>
              <Typography variant="body1">
                {unbilledWorkCount} shifts
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Bills Generated
              </Typography>
              <Typography variant="body1">
                {bills.length} bills
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Payrolls
              </Typography>
              <Typography variant="body1">
                {payrolls.length} payrolls
              </Typography>
            </Box>
          </Box>

          {billingPeriod.description && (
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                Description
              </Typography>
              <Typography variant="body1">
                {billingPeriod.description}
              </Typography>
            </Box>
          )}

          <Box mt={3} display="flex" gap={2} flexWrap="wrap">
            {canImportWork() && (
              <Button
                variant="outlined"
                startIcon={<ImportIcon />}
                onClick={handleImportCompletedWork}
                disabled={importing}
              >
                {importing ? <CircularProgress size={20} /> : 'Import Completed Work'}
              </Button>
            )}

            {canGenerateBills() && (
              <Button
                variant="contained"
                startIcon={<BillIcon />}
                onClick={handleGenerateBills}
                disabled={generating}
              >
                {generating ? <CircularProgress size={20} /> : 'Generate Bills'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      <Alert
        severity="info"
        icon={<InvoiceIcon />}
        sx={{ mb: 3 }}
        action={
          <Button
            color="inherit"
            size="small"
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate('/billing/client-invoices')}
          >
            Open Client Invoices
          </Button>
        }
      >
        Client invoices are no longer generated from billing periods. Create them from{' '}
        <strong>Client Invoices</strong> by selecting a client and the completed shifts you
        want to bill — rates come from the client base rate and the ClientCharge segments
        configured in Billing Rates.
      </Alert>

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
            <Tab
              label={`Completed Work (${completedWork.length})`}
              icon={<WorkIcon />}
              iconPosition="start"
            />
            <Tab
              label={`Bills (${bills.length})`}
              icon={<BillIcon />}
              iconPosition="start"
            />
            <Tab
              label={`Payroll (${payrolls.length})`}
              icon={<PayrollIcon />}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          <CompletedWorkView
            completedWork={completedWork}
            billingPeriod={billingPeriod}
            onRefresh={() => loadBillingPeriodData(billingPeriod.id)}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <BillsView
            bills={bills}
            billingPeriod={billingPeriod}
            onRefresh={() => loadBillingPeriodData(billingPeriod.id)}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <PayrollView
            payrolls={payrolls}
            billingPeriod={billingPeriod}
            onRefresh={() => loadBillingPeriodData(billingPeriod.id)}
          />
        </TabPanel>
      </Card>
    </Box>
    </PageLayout>
  );
};

export default BillingPeriodDetailPage;